import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { AuditLogAction, AuditLogDomain, AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import {
    auditLogRecipient,
    auditLogTimeLabel,
    humanizeAuditLogField,
    type AuditLogTimeLabel,
} from "./auditLogPresentation";

export type AuditLogLabels = {
    action: (action: AuditLogAction) => string;
    actorType: (actorType: string | null) => string;
    domain: (domain: AuditLogDomain) => string;
    entityType: (entityType: string) => string;
    scopeType: (rootType: string | null) => string;
    relativeTime: (occurredAt: string, now?: Date) => string;
    utcDay: (occurredAt: string) => string;
    utcTime: (occurredAt: string) => string;
    utcTimestamp: (occurredAt: string) => string;
    shortUtc: (occurredAt: string) => string;
    recipientPhrase: (entry: AuditLogEntry) => string | null;
    recipientHelper: (action: AuditLogAction) => string;
    changedField: (field: string) => string;
};

const UTC = "UTC";

export const useAuditLogLabels = (): AuditLogLabels => {
    const { t, i18n } = useTranslation();

    const formatters = useMemo(
        () => ({
            utcTimeFormatter: new Intl.DateTimeFormat(i18n.language, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hourCycle: "h23",
                timeZone: UTC,
            }),
            utcDayFormatter: new Intl.DateTimeFormat(i18n.language, {
                day: "2-digit",
                month: "2-digit",
                timeZone: UTC,
            }),
            utcTimestampFormatter: new Intl.DateTimeFormat(i18n.language, {
                dateStyle: "long",
                timeStyle: "medium",
                hourCycle: "h23",
                timeZone: UTC,
            }),
            shortUtcFormatter: new Intl.DateTimeFormat(i18n.language, {
                day: "numeric",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hourCycle: "h23",
                timeZone: UTC,
            }),
        }),
        [i18n.language],
    );

    return useMemo(() => {
        const actionLabels: Record<AuditLogAction, string> = {
            create: t("settings.audit_log.action.create", "Creation"),
            update: t("settings.audit_log.action.update", "Update"),
            delete: t("settings.audit_log.action.delete", "Deletion"),
        };

        const entityTypeLabels: Record<string, string> = {
            "role-organization-grant": t("settings.audit_log.entity_type.role_organization", "Role in an organization"),
            "role-platform-grant": t("settings.audit_log.entity_type.role_platform", "Role on the platform"),
            user: t("settings.audit_log.entity_type.user", "User"),
            application: t("settings.audit_log.entity_type.application", "Connected application"),
            "webhook-config": t("settings.audit_log.entity_type.webhook", "Webhook"),
            "service-account": t("settings.audit_log.entity_type.service_account", "Service account"),
            "service-credential": t("settings.audit_log.entity_type.service_credential", "Access key"),
        };

        const changedFieldLabels: Record<string, string> = {
            identity: t("settings.audit_log.field_name.identity", "Recipient"),
            assignedByIdentity: t("settings.audit_log.field_name.assigned_by", "Assigned by"),
            assignedAt: t("settings.audit_log.field_name.assigned_at", "Assigned on"),
            organization: t("settings.audit_log.field_name.organization", "Organization"),
            role: t("settings.audit_log.field_name.role", "Role"),
            active: t("settings.audit_log.field_name.active", "Active"),
            email: t("settings.audit_log.field_name.email", "Email"),
            firstname: t("settings.audit_log.field_name.firstname", "First name"),
            lastname: t("settings.audit_log.field_name.lastname", "Last name"),
            phone: t("settings.audit_log.field_name.phone", "Phone"),
            password: t("settings.audit_log.field_name.password", "Password"),
            is_verified: t("settings.audit_log.field_name.is_verified", "Email verified"),
            archivedAt: t("settings.audit_log.field_name.archived_at", "Archived on"),
            createdAt: t("settings.audit_log.field_name.created_at", "Created on"),
            name: t("settings.audit_log.field_name.name", "Name"),
            id: t("settings.audit_log.field_name.id", "Identifier"),
            description: t("settings.audit_log.field_name.description", "Description"),
            clientId: t("settings.audit_log.field_name.client_id", "Client identifier"),
            secretKey: t("settings.audit_log.field_name.secret_key", "Secret key"),
            hashedSecret: t("settings.audit_log.field_name.hashed_secret", "Secret"),
            rotatedAt: t("settings.audit_log.field_name.rotated_at", "Rotated on"),
            expiresAt: t("settings.audit_log.field_name.expires_at", "Expires on"),
            revokedAt: t("settings.audit_log.field_name.revoked_at", "Revoked on"),
            isDisabled: t("settings.audit_log.field_name.is_disabled", "Disabled"),
            allowedCidrs: t("settings.audit_log.field_name.allowed_cidrs", "Allowed IP ranges"),
            endpoint: t("settings.audit_log.field_name.endpoint", "Endpoint"),
            headers: t("settings.audit_log.field_name.headers", "Headers"),
        };

        const domainLabels: Record<AuditLogDomain, string> = {
            role: t("settings.audit_log.domain.role", "Roles"),
            integration: t("settings.audit_log.domain.integration", "Integrations"),
            user: t("settings.audit_log.domain.user", "Users"),
        };

        const recipientHelpers: Record<AuditLogAction, string> = {
            create: t("settings.audit_log.recipient_helper.create", "received this role"),
            update: t("settings.audit_log.recipient_helper.update", "holds this role"),
            delete: t("settings.audit_log.recipient_helper.delete", "no longer holds this role"),
        };

        const renderTimeLabel = (label: AuditLogTimeLabel): string => {
            if (label.kind === "seconds") {
                return t("settings.audit_log.time.now", "just now");
            }

            if (label.kind === "minutes") {
                return t("settings.audit_log.time.minutes", "{{minutes}}min ago", { minutes: label.value });
            }

            if (label.kind === "hours") {
                return t("settings.audit_log.time.hours", "{{hours}}h ago", { hours: label.value });
            }

            return t("settings.audit_log.time.days", "{{days}}d ago", { days: label.value });
        };

        return {
            action: (action) => actionLabels[action],
            actorType: (actorType) =>
                actorType === "service" ? t("settings.audit_log.actor_type.service", "Service account")
                : actorType === "human" ? t("settings.audit_log.actor_type.human", "Human account")
                : t("settings.audit_log.actor_type.unattributed", "Unattributed"),
            entityType: (entityType) => entityTypeLabels[entityType] ?? entityType,
            scopeType: (rootType) =>
                rootType === null || rootType === "" ? t("settings.audit_log.scope_type.platform", "Platform")
                : rootType === "organization" ? t("settings.audit_log.scope_type.organization", "Organization")
                : (entityTypeLabels[rootType] ?? rootType),
            relativeTime: (occurredAt, now = new Date()) => renderTimeLabel(auditLogTimeLabel(occurredAt, now)),
            utcDay: (occurredAt) => formatters.utcDayFormatter.format(new Date(occurredAt)),
            utcTime: (occurredAt) => formatters.utcTimeFormatter.format(new Date(occurredAt)),
            shortUtc: (occurredAt) => formatters.shortUtcFormatter.format(new Date(occurredAt)),
            utcTimestamp: (occurredAt) =>
                t("settings.audit_log.timestamp_utc", "{{timestamp}} (UTC)", {
                    timestamp: formatters.utcTimestampFormatter.format(new Date(occurredAt)),
                }),
            domain: (domain) => domainLabels[domain],
            recipientPhrase: (entry) => {
                const recipient = auditLogRecipient(entry);

                if (recipient === null) {
                    return null;
                }

                if (entry.action === "create") {
                    return t("settings.audit_log.recipient.create", "assigned to {{recipient}}", { recipient });
                }

                if (entry.action === "delete") {
                    return t("settings.audit_log.recipient.delete", "removed from {{recipient}}", { recipient });
                }

                return t("settings.audit_log.recipient.update", "held by {{recipient}}", { recipient });
            },
            recipientHelper: (action) => recipientHelpers[action],
            changedField: (field) => changedFieldLabels[field] ?? humanizeAuditLogField(field),
        };
    }, [t, formatters]);
};
